/**
 * The valley, alive: the painting, the weather, the water and the residents.
 *
 * A port of the wall display's scene (apps/display/public/scene.js and the
 * parts of scene.css still in force), built to fill whatever box it is put in
 * rather than the whole window -- so the same component is the full-screen
 * valley and, later, the card on a child's page.
 *
 * WHAT IS PORTED, layer by layer, bottom to top:
 *
 *   the painting     crossfaded, decoded before it is shown
 *   wind light       a slow sheen across the grass, stronger as the valley lives
 *   water            reflections in the pools, highlights down the falls
 *   residents        up to 30 animals on routes traced against the painting,
 *                    and the family's children at their activity stations
 *   night wash       a blue multiply over everything after dark (wide screens)
 *   stars, fireflies at night
 *
 * WHAT IS DELIBERATELY NOT: the display's drifting petals and `#ambience`
 * layer, which its own current CSS hides (`#ambience{visibility:hidden}`),
 * and `#flying-birds`, which nothing populates. Porting switched-off features
 * would only be porting their bugs.
 *
 * PERFORMANCE, and the one rule this file is built round: React draws the
 * DOM once and never re-renders per frame. The animation loop moves elements
 * by writing their styles directly, through refs, the way the display does.
 * Thirty residents through React state at sixty frames a second would be the
 * slowest possible way to do this, and the most battery-hungry on the phones
 * and tablets this will be left running on.
 *
 * Motion is a pure function of one clock (see wildlife/motion.ts), so pausing
 * -- a hidden tab, reduced motion -- costs nothing and resumes exactly where
 * it stopped. `prefers-reduced-motion` gets a still valley with everybody in
 * place, never an empty one.
 */

import { lazy, Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { assetUrl, applySheet, cellCss } from './sprites';
import { sceneFor, stageInfo, STAGE_WORDS, currentScene, SCENES, portraitForScene } from './scene';
import { ATLAS, nativeFacing, type Species } from './wildlife/atlas';
import { loadSheet } from './loader';
import { arrange, activeAt, camera, clear, perspective, type Placed } from './wildlife/layout';
import { itinerary, sample } from './wildlife/motion';
import { describe as ecologyOf, presence } from './wildlife/ecology';
import { Water } from './water';
import { SHEETS, type SheetId } from './art.generated';
import { bodyBox, capacity, dayNumber } from '../avatar/activities';
import { startMotion } from '../avatar/painted';
import { usePaintedSvg } from '../avatar/painting';
import { cast, lineup, lineupRoom, ON_SCREEN, ROTATE, type Resident } from '../avatar/residents';

/** The longest step the clock may take, so a stalled frame never teleports anyone. */
const MAX_STEP = 0.08;
const SceneResidents = lazy(() => import('./SceneResidents').then(module => ({ default: module.SceneResidents })));

interface Props {
  balance: number;
  /** The family's garden target, which decides the painting's stage. */
  target?: number;
  night: boolean;
  /** Stop the clock, e.g. while something covers the valley. */
  paused?: boolean;
  /**
   * The children standing in the valley: approved, with a character. A
   * child's own page passes only them -- a sibling is not theirs to see.
   */
  people?: Resident[];
  /**
   * Tapping a child. Given, the children become buttons -- the display's
   * resident card, see FullValley. Not given, they are scenery.
   */
  onPick?: (personId: string) => void;
  /** HUD, controls -- drawn above the scene. */
  children?: ReactNode;
}

export function LivingValley({ balance, target, night, paused = false, people = [], onPick, children }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const size = useSize(box);
  const reduced = useReducedMotion();
  const visible = usePageVisible();

  // The real hour, so a time of day of its own (a dusk painting, say:
  // content/valley.toml) shows when it should -- held to the light the
  // caller chose, which is what the animals and the sky follow.
  const scene = sceneFor(balance, {
    hour: new Date().getHours(),
    night,
    width: size.width,
    height: size.height,
    ...(target ? { target } : {}),
  });
  const { life, level } = ecologyOf(balance);
  const selectedScene = currentScene();
  const customLayout = Boolean(SCENES.find(s => s.id === selectedScene)?.composition);
  const layoutKey = `${selectedScene}/${portraitForScene(size.width, size.height) ? 'portrait' : 'landscape'}`;
  // NOTHING ELSE LOADS UNTIL THE PAINTING HAS (2026-10-12). The children and
  // the animals wait for it, then arrive through the art queue (loader.ts),
  // so the first thing anyone sees is the valley itself, not a scatter of
  // half-loaded sprites over an empty frame. A slow or failed painting gives
  // way after a few seconds rather than holding everything back for good.
  const [readyLayout, setReadyLayout] = useState<string | null>(null);
  const ready = readyLayout === layoutKey;
  useEffect(() => {
    const timer = window.setTimeout(() => setReadyLayout(layoutKey), 4000);
    return () => window.clearTimeout(timer);
  }, [layoutKey]);

  return (
    <div
      ref={box}
      className={`living-valley${night?' is-night':''}${paused?' paused':''}`}
      data-level={level}
      style={{ ['--life' as string]: String(life) }}
    >
      <div className="valley-landscape">
        <Painting key={layoutKey} name={scene.name} alt={STAGE_WORDS[scene.stage]} animate={!reduced} onReady={() => setReadyLayout(layoutKey)} />
        <div className="valley-wind" aria-hidden="true" />
      </div>
      {size.width > 0 && customLayout ? (
        <Suspense fallback={null}><SceneResidents scene={selectedScene} stage={scene.stage} night={night}
          width={size.width} height={size.height} people={people} ready={ready}
          running={!paused && !reduced && visible} {...(onPick ? { onPick } : {})} /></Suspense>
      ) : size.width > 0 ? (
        <Life
          width={size.width}
          height={size.height}
          life={life}
          night={night}
          running={!paused && !reduced && visible}
          ready={ready}
          people={people}
          dormant={stageInfo(scene.stage).dormant}
          {...(onPick ? { onPick } : {})}
        />
      ) : null}
      <div className="valley-night-wash" aria-hidden="true" />
      <Stars />
      <Fireflies on={night && life > 0.1 && !reduced} life={life} />
      {children}
    </div>
  );
}

/**
 * The painting, crossfaded the way the display does it.
 *
 * Two layers. A new painting goes into the one at the back, and only when it
 * has LOADED does it fade over the front one -- so nightfall, or a family
 * crossing into a new stage, is a slow change rather than a snap, and never a
 * flash of nothing while a 300 KB image arrives. `<picture>` in each layer so
 * the browser still picks AVIF or WebP for itself.
 *
 * The first painting appears at once. The display fades in from black on
 * boot over seven seconds, which suits a screen that is switched on in the
 * morning and is far too slow for somebody opening an app.
 */
function Painting({ name, alt, animate, onReady }: { name: string; alt: string; animate: boolean; onReady?: () => void }) {
  const [layers, setLayers] = useState<[string, string | null]>([name, null]);
  const [front, setFront] = useState<0 | 1>(0);
  const wanted = useRef(name);
  wanted.current = name;

  useEffect(() => {
    setLayers((current) => {
      if (current[front] === name) return current;
      const next: [string, string | null] = [...current] as [string, string | null];
      next[front === 0 ? 1 : 0] = name;
      return next;
    });
  }, [name, front]);

  return (
    <>
      {layers.map((file, index) =>
        file ? (
          <picture
            key={index}
            className="valley-painting"
            style={{
              opacity: index === front ? 1 : 0,
              transitionDuration: animate ? undefined : '0s',
            }}
          >
            <source srcSet={assetUrl(file, 'avif')} type="image/avif" />
            <source srcSet={assetUrl(file, 'webp')} type="image/webp" />
            <img
              src={assetUrl(file, 'webp')}
              alt={index === front ? alt : ''}
              decoding="async"
              draggable={false}
              // The one thing on screen that must not wait behind anything.
              {...{ fetchpriority: index === 0 ? 'high' : 'auto' }}
              onLoad={() => {
                onReady?.();
                // Only flip to a layer that holds what is wanted NOW. A slow
                // load that finishes after the balance has moved on again must
                // not drag the valley back to a stale stage.
                if (file === wanted.current && index !== front) setFront(index as 0 | 1);
              }}
            />
          </picture>
        ) : null,
      )}
    </>
  );
}

interface Node {
  route: HTMLDivElement | null;
  heading: HTMLDivElement | null;
  sprite: HTMLDivElement | null;
  shadow: HTMLDivElement | null;
  sheet?: SheetId;
  frameKey?: string;
}

/** The residents and the water, on one clock. */
function Life({
  width,
  height,
  life,
  night,
  running,
  ready,
  people,
  dormant = false,
  onPick,
}: {
  width: number;
  height: number;
  life: number;
  night: boolean;
  running: boolean;
  /** The painting is in: the residents may start loading. */
  ready: boolean;
  people: Resident[];
  /** The Dormant valley: the children stand in a line instead of playing. */
  dormant?: boolean;
  onPick?: (personId: string) => void;
}) {
  // The children first: where they are decides where the animals may go.
  // A larger family takes turns, every two minutes, as the display does.
  const [turn, setTurn] = useState(0);
  const room = dormant ? lineupRoom(width, height) : Math.min(capacity(width, height), ON_SCREEN);
  const rotates = people.length > room;
  useEffect(() => {
    setTurn(0);
    if (!rotates) return;
    const timer = window.setInterval(() => setTurn((n) => n + room), ROTATE * 1000);
    return () => window.clearInterval(timer);
  }, [rotates, room]);
  // The deal changes at dawn, at dusk and at midnight; the parent re-renders
  // at least that often (see FullValley's clock), so the day is a dependency.
  const day = dayNumber();
  const kids = useMemo(
    () =>
      people.length
        ? dormant
          ? lineup(people, width, height, turn)
          : cast(people, width, height, night, turn)
        : { shown: [] as Resident[], placed: [] },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [people, width, height, night, turn, day, dormant],
  );

  const arranged = useMemo(() => arrange(width, height), [width, height]);
  // Who is out right now, by health and by the hour.
  const out = arranged.map((one) =>
    one.enabled ? presence(life, one.threshold, activeAt(one.name, night)) > 0 : false,
  );
  const outKey = out.map((o) => (o ? 1 : 0)).join('');
  const kidsKey = kids.placed.map((k) => `${k.id}:${Math.round(k.x)}:${Math.round(k.y)}:${Math.round(k.width)}`).join('|');
  // Routes kept clear of the children and of each other (see `clear`).
  const placed = useMemo(
    () =>
      clear(arranged, kids.placed.map(bodyBox), (i) => out[i] ?? false).map((one) =>
        one.enabled ? { ...one, steps: itinerary(one.name, one) } : one,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [arranged, outKey, kidsKey],
  );
  const nodes = useRef<Node[]>([]);
  const water = useRef<Water | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  // Survives re-arrangement on resize, so turning a tablet round does not send
  // every animal back to the start of its walk.
  const clock = useRef(0);
  const shown = useRef<boolean[]>([]);

  // ANIMALS ARRIVE ONE SPECIES AT A TIME, each once all of its sheets --
  // walk, idle, turn, graze -- are decoded, so none is ever drawn with a
  // missing pose. In resident order, through the art queue behind the
  // children (loader.ts), so the valley fills in rather than appearing in
  // one heavy burst.
  const [admitted, setAdmitted] = useState<ReadonlySet<Species>>(() => new Set());
  const wanted = [...new Set(placed.filter((one, i) => one.enabled && out[i]).map((one) => one.name))].join(',');
  useEffect(() => {
    if (!ready || !wanted) return;
    let live = true;
    for (const name of wanted.split(',') as Species[]) {
      void loadSpecies(name).then(
        () => live && setAdmitted((had) => (had.has(name) ? had : new Set([...had, name]))),
        () => live && setAdmitted((had) => (had.has(name) ? had : new Set([...had, name]))),
      );
    }
    return () => {
      live = false;
    };
  }, [ready, wanted]);

  const opacities = placed.map((one) =>
    one.enabled && admitted.has(one.name) ? presence(life, one.threshold, activeAt(one.name, night)) : 0,
  );
  shown.current = opacities.map((o) => o > 0);

  useEffect(() => {
    if (!canvas.current) return;
    // Refresh can preserve refs while replacing Water's class. Do not retain
    // an instance with the previous cascade geometry during art review.
    if (!(water.current instanceof Water)) water.current = new Water(canvas.current);
    water.current.resize(width, height);
  }, [width, height, Water]);

  useEffect(() => {
    const view = camera(width, height);

    function draw(): void {
      const seconds = clock.current;
      placed.forEach((motion, i) => {
        const node = nodes.current[i];
        if (!motion.enabled || !node?.route || !shown.current[i]) return;
        paint(node, motion, sample(motion.name, seconds, motion), view);
      });
      water.current?.render(seconds, life);
    }

    draw();
    if (!running) return;

    let frame = 0;
    let last: number | null = null;
    const tick = (now: number) => {
      if (last !== null) clock.current += Math.min((now - last) / 1000, MAX_STEP);
      last = now;
      draw();
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [placed, running, life, width, height]);

  return (
    <>
      <canvas ref={canvas} className="valley-water" aria-hidden="true"
              style={{ opacity: life === 0 ? 0 : life * 0.8 }} />
      <div className="valley-wildlife">
        {placed.map((motion, i) => {
          if (!motion.enabled) return null;
          const rig = ATLAS[motion.name];
          const grounded = !rig.flying && !rig.climbs;
          return (
            <div
              key={i}
              aria-hidden="true"
              className="critter-route"
              data-species={motion.name}
              data-habitat={motion.habitat}
              style={{ opacity: opacities[i], ['--size' as string]: `${motion.size}px` }}
              ref={(el) => {
                (nodes.current[i] ??= { route: null, heading: null, sprite: null, shadow: null }).route = el;
              }}
            >
              {grounded ? (
                <div
                  className={motion.name === 'duck' ? 'critter-wake' : 'critter-shadow'}
                  ref={(el) => {
                    (nodes.current[i] ??= { route: null, heading: null, sprite: null, shadow: null }).shadow = el;
                  }}
                />
              ) : null}
              <div
                className="critter-heading"
                ref={(el) => {
                  (nodes.current[i] ??= { route: null, heading: null, sprite: null, shadow: null }).heading = el;
                }}
              >
                <div
                  className="critter"
                  ref={(el) => {
                    (nodes.current[i] ??= { route: null, heading: null, sprite: null, shadow: null }).sprite = el;
                  }}
                />
              </div>
            </div>
          );
        })}
        {ready && kids.placed.length ? (
          <Residents shown={kids.shown} placed={kids.placed} {...(onPick ? { onPick } : {})} />
        ) : null}
      </div>
    </>
  );
}

/**
 * The children, each at their station doing the period's activity: the DOM
 * half of the display's avatar-residents.js. Placement is `cast`, which is
 * held to the display's by residents.parity.test.ts; the breathing, blinking
 * and frame changes are the painted characters' own clock (`startMotion`).
 *
 * Inside the wildlife layer, unstacked, so a child and a fox are drawn in
 * front of each other by who is nearer -- as on the wall.
 */
function Residents({
  shown,
  placed,
  onPick,
}: {
  shown: Resident[];
  placed: ReturnType<typeof cast>['placed'];
  onPick?: (personId: string) => void;
}) {
  useEffect(() => startMotion(), []);
  return (
    <div className="valley-residents">
      {placed.map((pose) => {
        const person = shown.find((p) => p.id === pose.id);
        return person && pose.activity ? (
          <ResidentNode
            key={person.id}
            person={person}
            activity={pose.activity.id}
            x={pose.x}
            y={pose.y}
            width={pose.width}
            mirror={Boolean(pose.mirror)}
            {...(onPick ? { onPick } : {})}
          />
        ) : null;
      })}
    </div>
  );
}

function ResidentNode({
  person,
  activity,
  x,
  y,
  width,
  mirror,
  onPick,
}: {
  person: Resident;
  activity: string;
  x: number;
  y: number;
  width: number;
  /** Turned round: a fishing child on the far bank (see activities.ts). */
  mirror: boolean;
  onPick?: (personId: string) => void;
}) {
  // A painted SVG string, built from small integers and fixed shapes; see
  // avatar/Character.tsx for why putting it in the page is safe.
  // The standing portrait is painted in the first outfit only (the faces
  // use it too); every other activity has all four.
  const spec = activity === 'standing' ? { ...person.avatar, outfit: 0 } : person.avatar;
  const svg = usePaintedSvg(spec, { size: width, activity, key: person.id });
  return (
    <div
      className={['avatar-resident', onPick ? 'can-press' : '', mirror ? 'is-mirrored' : ''].filter(Boolean).join(' ')}
      data-person={person.id}
      // A child is a thing you can ask about, not scenery -- when there is
      // somewhere to show the answer.
      {...(onPick
        ? {
            role: 'button',
            tabIndex: 0,
            'aria-label': `${person.name || 'This character'} — see their money and history`,
            onClick: () => onPick(person.id),
            onKeyDown: (event: React.KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onPick(person.id);
              }
            },
          }
        : { 'aria-hidden': true })}
      style={{
        transform: `translate3d(${x}px,${y}px,0)`,
        zIndex: Math.round(y),
        ['--avatar-width' as string]: `${width}px`,
      }}
      // eslint-disable-next-line react/no-danger -- see above
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

/** One resident, one frame. `renderWildlife` from scene.js, per animal. */
function paint(node: Node, motion: Placed, pose: ReturnType<typeof sample>, view: ReturnType<typeof camera>): void {
  const { route, heading, sprite, shadow } = node;
  if (!route || !heading || !sprite) return;

  route.style.transform = `translate3d(${pose.x.toFixed(2)}px,${pose.y.toFixed(2)}px,0)`;
  // Lower on the screen is nearer, and nearer is drawn in front.
  route.style.zIndex = String(Math.round(pose.y));
  route.dataset.behavior = pose.behavior;
  const sourceFacing = nativeFacing(motion.name, pose.cells, pose.frame);
  heading.style.transform = `scaleX(${pose.facing * sourceFacing})`;

  const { sheet, columns, rows, row } = pose.cells;
  if (node.sheet !== sheet) {
    applySheet(sprite, sheet);
    node.sheet = sheet;
  }
  const frameKey = `${sheet}:${row}:${pose.frame}`;
  if (node.frameKey !== frameKey) {
    const css = cellCss(columns, rows, pose.frame, row);
    sprite.style.backgroundSize = css.size;
    sprite.style.backgroundPosition = css.position;
    node.frameKey = frameKey;
  }

  // A resting bat hangs from its perch rather than sitting on it.
  const hanging = motion.size * 0.6 * pose.perch;
  sprite.style.transformOrigin = ATLAS[motion.name].climbs ? '50% 50%'
    : ATLAS[motion.name].flying ? '78% 55%' : '50% 94%';
  sprite.style.transform =
    `translateY(${(hanging - pose.lift).toFixed(2)}px) rotate(${pose.tilt.toFixed(2)}deg) scaleY(${pose.squash.toFixed(3)})`;

  // Size follows ground depth, never viewport width. Only meadow animals
  // recede as they walk; a squirrel stays one size all the way up a tree.
  if (motion.habitat === 'meadow') {
    const depth = (pose.y - view.y) / view.height;
    const size = (motion.size * perspective(depth)) / perspective(motion.depth);
    route.style.setProperty('--size', `${size.toFixed(2)}px`);
  }

  if (shadow) {
    const off = Math.min(1, pose.lift / Math.max(1, motion.height * 0.5));
    shadow.style.transform = `scale(${(1 - off * 0.45).toFixed(3)})`;
    shadow.style.opacity = Math.max(0.12, 0.4 - off * 0.28).toFixed(3);
  }
}

/** Forty stars, placed the same way every time, as the display places them. */
function Stars() {
  return (
    <div className="valley-stars" aria-hidden="true">
      {Array.from({ length: 40 }, (_, i) => (
        <i
          key={i}
          className="star-point"
          style={{
            left: `${(i * 37.3) % 100}%`,
            top: `${(i * 19.7) % 90}%`,
            animationDelay: `${-i * 0.7}s`,
            ['--twinkle' as string]: `${3 + (i % 6)}s`,
          }}
        />
      ))}
    </div>
  );
}

function Fireflies({ on, life }: { on: boolean; life: number }) {
  return (
    <div className="valley-fireflies" aria-hidden="true" style={{ opacity: on ? life : 0 }}>
      {Array.from({ length: 25 }, (_, i) => (
        <i
          key={i}
          className="firefly"
          style={{
            left: `${8 + ((i * 7.3) % 84)}%`,
            top: `${48 + ((i * 3.7) % 43)}%`,
            ['--delay' as string]: `${-i * 0.8}s`,
          }}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------

/** The box's own size, so this works inside a card as well as full screen. */
function useSize(ref: React.RefObject<HTMLElement | null>) {
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      // Rounded: sub-pixel jitter from a scrollbar appearing would otherwise
      // re-arrange every route and reset nothing visible for a lot of work.
      const next = { width: Math.round(rect.width), height: Math.round(rect.height) };
      setSize((old) => (old.width === next.width && old.height === next.height ? old : next));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);
  return size;
}

function useReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(
    () => typeof matchMedia !== 'undefined' && matchMedia(query).matches,
  );
  useEffect(() => {
    if (typeof matchMedia === 'undefined') return;
    const list = matchMedia(query);
    const change = () => setReduced(list.matches);
    list.addEventListener?.('change', change);
    return () => list.removeEventListener?.('change', change);
  }, []);
  return reduced;
}

/** A hidden tab animates nothing. Browsers throttle it anyway; this stops it. */
function usePageVisible(): boolean {
  const [visible, setVisible] = useState(() => document.visibilityState !== 'hidden');
  useEffect(() => {
    const change = () => setVisible(document.visibilityState !== 'hidden');
    document.addEventListener('visibilitychange', change);
    return () => document.removeEventListener('visibilitychange', change);
  }, []);
  return visible;
}

/** Every sheet one species is drawn from, decoded, in the art queue. */
function loadSpecies(name: Species): Promise<void> {
  const rig = ATLAS[name];
  const sheets = new Set<SheetId>([rig.walk.sheet, rig.idle.sheet, rig.turn.sheet, ...(rig.graze ? [rig.graze.sheet] : [])]);
  return Promise.all(
    [...sheets].map((id) => {
      const file = SHEETS[id].file;
      return loadSheet(assetUrl(file, 'avif'), assetUrl(file, 'webp'), 2);
    }),
  ).then(() => undefined);
}
